import * as Sentry from '@sentry/react';
import { matchMarketingRoute } from '@/marketing/routes';

type MonitoringConfig = { enabled: boolean; dsn?: string; environment?: string; release?: string };
type FailureStage = 'react_render' | 'hydration' | 'turnstile_load' | 'turnstile_verification' | 'sample_submit' | 'radar_request';
type FailureKind = 'runtime' | 'network' | 'offline' | 'timeout' | 'http' | 'invalid_response' | 'load' | 'verification' | 'delivery_unknown' | 'delivery_failed';
type BrowserFailure = { stage: FailureStage; kind: FailureKind; status?: number; method?: string; route?: string; requestId?: string };
type BrowserOptions = NonNullable<Parameters<typeof Sentry.init>[0]>;

const stages = new Set(['react_render', 'hydration', 'turnstile_load', 'turnstile_verification', 'sample_submit', 'radar_request']);
const kinds = new Set(['runtime', 'network', 'offline', 'timeout', 'http', 'invalid_response', 'load', 'verification', 'delivery_unknown', 'delivery_failed']);
const errorTypes = new Set(['Error', 'TypeError', 'ReferenceError', 'RangeError', 'SyntaxError', 'URIError', 'EvalError', 'DOMException', 'AbortError', 'TimeoutError']);
const apiSegments = new Set(['api', 'radar', 'marketing', 'inquiries', 'session', 'workspaces', 'act', 'contacts', 'reveal', 'drafts', 'gmail', 'translations', 'signals', 'feedback', 'watches', 'confirm', 'signal-plan', 'brief-hypothesis', 'brief-review', 'brief', 'search-runs', 'email-signals', 'refresh', 'monitoring-events', 'companies', 'monitoring-advice', 'outreach', 'contact-ledger', 'suppression', 'google-grant', 'abandon', 'expo', 'sessions', 'email', 'claim', 'retry-search', 'retry-mail']);
let initialized = false;

function pageRoute(path: string): string {
  const pathname = path.split(/[?#]/)[0];
  const marketing = matchMarketingRoute(pathname);
  if (marketing) return marketing.path;
  if (/^\/buyer-radar\/workspaces\/[^/]+\/?$/.test(pathname)) return '/buyer-radar/workspaces/:id';
  if (/^\/buyer-radar\/expo\/claim\/[^/]+\/?$/.test(pathname)) return '/buyer-radar/expo/claim/:id';
  return ['/buyer-radar/app', '/buyer-radar/expo', '/privacy', '/beta', '/welcome-beta', '/onboarding'].includes(pathname.replace(/\/$/, '')) ? pathname.replace(/\/$/, '') : 'other';
}

function apiRoute(value: unknown): string | undefined {
  if (typeof value !== 'string' || !/^\/api\/(radar|marketing)(\/|$)/.test(value)) return undefined;
  return value.split(/[?#]/)[0].split('/').map(part => !part || apiSegments.has(part) ? part : ':id').join('/');
}

function browserTags(): Record<string, string> {
  const agent = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const browser = agent.match(/(Edg)\/(\d{1,3})/) || agent.match(/(Firefox|Chrome)\/(\d{1,3})/) || agent.match(/(Version)\/(\d{1,3}).*Safari\//);
  return {
    browser_family: browser ? ({ Edg: 'Edge', Firefox: 'Firefox', Chrome: 'Chrome', Version: 'Safari' }[browser[1]] || 'other') : 'other',
    browser_major: browser?.[2] || 'unknown',
    network_online: typeof navigator === 'undefined' ? 'unknown' : String(navigator.onLine),
  };
}

function safeTags(tags: Record<string, unknown> = {}): Record<string, string> {
  const result: Record<string, string> = {};
  if (stages.has(String(tags.failure_stage))) result.failure_stage = String(tags.failure_stage);
  if (kinds.has(String(tags.failure_kind))) result.failure_kind = String(tags.failure_kind);
  if (/^[45]\d\d$/.test(String(tags.http_status))) result.http_status = String(tags.http_status);
  if (['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD'].includes(String(tags.method))) result.method = String(tags.method);
  if (/^[a-f\d]{8}-(?:[a-f\d]{4}-){3}[a-f\d]{12}$/i.test(String(tags.request_id))) result.request_id = String(tags.request_id);
  const route = apiRoute(tags.api_route);
  if (route) result.api_route = route;
  return result;
}

export function browserMonitoringOptions(config: MonitoringConfig, pathname?: string, origin?: string): BrowserOptions {
  return {
    dsn: config.dsn,
    environment: ['production', 'staging', 'development', 'test', 'preview'].includes(config.environment || '') ? config.environment : 'unknown',
    release: /^(?:[a-f\d]{7,64}|local|unknown)$/i.test(config.release || '') ? config.release : 'unknown',
    defaultIntegrations: false,
    integrations: [Sentry.globalHandlersIntegration(), Sentry.browserApiErrorsIntegration(), Sentry.dedupeIntegration()],
    sendDefaultPii: false, maxBreadcrumbs: 0, enableLogs: false,
    sendClientReports: false, tracePropagationTargets: [], attachStacktrace: true,
    beforeSend(event, hint) {
      hint.attachments = [];
      if (event.type) return null;
      const siteOrigin = origin || (typeof window !== 'undefined' ? window.location.origin : 'https://mindstreamapp.ai');
      const tags: Record<string, string> = { component: 'web-frontend', ...browserTags(), ...safeTags(event.tags), page_route: pageRoute(pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/')) };
      const values = event.exception?.values?.slice(0, 5).map(value => {
        const type = errorTypes.has(value.type || '') ? value.type! : 'Error';
        const frames = (value.stacktrace?.frames || []).flatMap(frame => {
          try {
            const file = new URL(frame.filename || '', siteOrigin);
            if (file.origin !== siteOrigin || !/^\/assets\/[a-z\d_.-]+\.(?:js|mjs)$/i.test(file.pathname)) return [];
            return [{ filename: `${siteOrigin}${file.pathname}`, in_app: true,
              ...(Number.isInteger(frame.lineno) && frame.lineno! > 0 ? { lineno: frame.lineno } : {}),
              ...(Number.isInteger(frame.colno) && frame.colno! >= 0 ? { colno: frame.colno } : {}),
            }];
          } catch { return []; }
        }).slice(-50);
        return { type, value: `Browser ${type}`, ...(frames.length ? { stacktrace: { frames } } : {}),
          mechanism: { type: value.mechanism?.handled === false ? 'unhandled' : 'handled', handled: value.mechanism?.handled !== false } };
      });
      return {
        type: undefined,
        event_id: /^[a-f\d]{32}$/i.test(event.event_id || '') ? event.event_id : undefined,
        timestamp: Number.isFinite(event.timestamp) ? event.timestamp : undefined,
        platform: 'javascript', level: event.level === 'warning' ? 'warning' : 'error',
        environment: ['production', 'staging', 'development', 'test', 'preview'].includes(config.environment || '') ? config.environment : 'unknown',
        release: /^(?:[a-f\d]{7,64}|local|unknown)$/i.test(config.release || '') ? config.release : 'unknown',
        tags, ...(values?.length ? { exception: { values } } : { message: 'Browser operation failed' }),
        ...(tags.failure_stage && tags.failure_stage !== 'react_render' && tags.failure_stage !== 'hydration'
          ? { fingerprint: ['browser-operation', tags.failure_stage, tags.failure_kind || 'runtime', tags.api_route || '', tags.http_status || ''] } : {}),
      };
    },
  };
}

export function initializeBrowserMonitoring(config: MonitoringConfig): void {
  if (initialized || !config.enabled || !config.dsn || typeof window === 'undefined') return;
  try {
    Sentry.init(browserMonitoringOptions(config));
    initialized = true;
  } catch { initialized = false; }
}

export function networkFailureKind(error: unknown): FailureKind {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
  return error instanceof Error && error.name === 'TimeoutError' ? 'timeout' : 'network';
}

export function reportBrowserFailure(failure: BrowserFailure): void {
  if (!initialized) return;
  try {
    const tags = safeTags({ failure_stage: failure.stage, failure_kind: failure.kind, http_status: failure.status,
      api_route: failure.route, method: failure.method, request_id: failure.requestId });
    Sentry.captureMessage(`Browser ${failure.stage}: ${failure.kind}`, { level: ['network', 'offline', 'timeout', 'verification'].includes(failure.kind) ? 'warning' : 'error', tags });
  } catch { return; }
}

export function reportRenderFailure(error: unknown, stage: 'react_render' | 'hydration' = 'react_render'): string | undefined {
  if (!initialized) return undefined;
  try { return Sentry.captureException(error, { tags: { failure_stage: stage, failure_kind: 'runtime' } }); }
  catch { return undefined; }
}
